import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, X, Paintbrush, GripVertical, EyeOff, Eye, Trash2, Plus, FileDown, RefreshCw } from 'lucide-react';
import axios from 'axios';
import API from './apiConfig';
import LVEditorModal from './LVEditorModal';
import { useModal } from './components/CustomModal';

// Safe value extraction - handles {value, basis, note} objects
function safeNum(v) { return (v && typeof v === 'object' && 'value' in v) ? Number(v.value) || 0 : Number(v) || 0; }
function safeStr(v) { return (v && typeof v === 'object') ? (v.value != null ? String(v.value) : JSON.stringify(v)) : (v != null ? String(v) : ''); }


const TABS = [
  { key: 'walls', label: 'Wände' },
  { key: 'rooms', label: 'Räume' },
  { key: 'windows', label: 'Fenster' },
  { key: 'doors', label: 'Türen' },
  { key: 'manual', label: 'Manuell' },
];

// ─── High-Res Crop Preview ────────────────────────────────────────
const PREVIEW_W = 420;
const PREVIEW_H = 320;

function CropPreview({ imageB64, imageSize, element }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  // Reset zoom/pan when element changes
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [element]);

  useEffect(() => {
    if (!imageB64) return;
    const img = new Image();
    img.onload = () => { imgRef.current = img; setReady(true); };
    img.src = `data:image/jpeg;base64,${imageB64}`;
  }, [imageB64]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !ready || !element) return;

    const ctx = canvas.getContext('2d');
    const imgW = imageSize?.width || img.width;
    const imgH = imageSize?.height || img.height;

    let pts = [];
    if (element.polygon) pts = element.polygon;
    else if (element.from_pt && element.to_pt) pts = [element.from_pt, element.to_pt];
    else if (element.hinge && element.closed_pt) {
      pts = [element.hinge, element.closed_pt];
      if (element.open_pt) pts.push(element.open_pt);
    }
    if (pts.length < 2) { ctx.fillStyle = '#0a0a0a'; ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H); return; }

    const xs = pts.map(p => p[0]);
    const ys = pts.map(p => p[1]);
    const pad = 3;
    const sx = Math.max(0, (Math.min(...xs) - pad) / 100) * imgW;
    const sy = Math.max(0, (Math.min(...ys) - pad) / 100) * imgH;
    const sw = Math.min(imgW - sx, ((Math.max(...xs) - Math.min(...xs) + pad * 2) / 100) * imgW);
    const sh = Math.min(imgH - sy, ((Math.max(...ys) - Math.min(...ys) + pad * 2) / 100) * imgH);

    canvas.width = PREVIEW_W;
    canvas.height = PREVIEW_H;
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);

    const baseScale = Math.min(PREVIEW_W / sw, PREVIEW_H / sh);
    const scale = baseScale * zoom;
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (PREVIEW_W - dw) / 2 + pan.x;
    const dy = (PREVIEW_H - dh) / 2 + pan.y;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

    // Crosshair (only at default zoom)
    if (zoom <= 1.1) {
      const cx = ((Math.min(...xs) + Math.max(...xs)) / 2 / 100 * imgW - sx) * scale + dx;
      const cy = ((Math.min(...ys) + Math.max(...ys)) / 2 / 100 * imgH - sy) * scale + dy;
      ctx.strokeStyle = 'rgba(59,130,246,0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(cx, cy, 14, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx - 18, cy); ctx.lineTo(cx + 18, cy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy - 18); ctx.lineTo(cx, cy + 18); ctx.stroke();
    }
  }, [element, ready, imageSize, zoom, pan]);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const handleMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
  };
  const handleMouseUp = () => { dragRef.current = null; };

  if (!element) return <div className="flex items-center justify-center h-full text-zinc-700 font-mono text-[10px]">Element anklicken</div>;
  return (
    <div className="relative w-full h-full" onWheel={handleWheel}>
      <canvas ref={canvasRef} width={PREVIEW_W} height={PREVIEW_H}
        className={`w-full h-full ${zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} />
      {zoom !== 1 && (
        <div className="absolute bottom-1 right-1 flex items-center gap-1">
          <span className="font-mono text-[9px] text-zinc-500 bg-zinc-900/80 px-1.5 py-0.5 rounded">{Math.round(zoom * 100)}%</span>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="font-mono text-[9px] text-blue-400 bg-zinc-900/80 px-1.5 py-0.5 rounded hover:bg-zinc-800">
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Inline Edit Cell ──────────────────────────────────────────────
function EditableCell({ value, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const ref = useRef(null);

  const start = () => { setVal(value || ''); setEditing(true); setTimeout(() => ref.current?.select(), 50); };
  const save = () => { setEditing(false); const n = parseFloat(String(val).replace(',', '.')); if (!isNaN(n) && n > 0) onSave(n); };

  if (editing) {
    return <input ref={ref} value={val} onChange={e => setVal(e.target.value)}
      onBlur={save} onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      className="w-full bg-zinc-800 border border-blue-500 rounded px-1 text-[10px] font-mono text-white text-right outline-none" autoFocus />;
  }
  return <span onClick={start} className={`cursor-pointer hover:text-blue-400 transition-colors ${className}`} title="Bearbeiten">{value || '—'}</span>;
}

// ─── Grouping helpers ──────────────────────────────────────────────
function groupWalls(walls) {
  const exterior = walls.filter(w => w.wall_type === 'exterior');
  const interior = walls.filter(w => w.wall_type !== 'exterior');
  const byThickness = (arr) => {
    const groups = {};
    arr.forEach(w => {
      const t = Math.round((w.thickness_m_final || w.thickness_m || 0) * 1000);
      const key = `${t}mm`;
      if (!groups[key]) groups[key] = { thickness_mm: t, items: [] };
      groups[key].items.push(w);
    });
    return Object.values(groups).sort((a, b) => b.thickness_mm - a.thickness_mm);
  };
  return [
    { type: 'exterior', label: 'Außenwände', groups: byThickness(exterior), total: exterior.length },
    { type: 'interior', label: 'Innenwände', groups: byThickness(interior), total: interior.length },
  ];
}

function groupOpenings(openings) {
  const groups = {};
  openings.forEach(o => {
    const w = Math.round((o.width_m_matched || o.width_m || 0) * 100);
    const h = Math.round((o.height_m_matched || 0) * 100);
    const key = o.type === 'door' ? `${w}x${h}_${o.din_anschlag || '?'}` : `${w}x${h}`;
    if (!groups[key]) groups[key] = { w, h, anschlag: o.din_anschlag, items: [] };
    groups[key].items.push(o);
  });
  return Object.values(groups).sort((a, b) => b.w - a.w || b.h - a.h);
}

// ─── Drag & Drop Row ───────────────────────────────────────────────
function DraggableRow({ element, children, onDrop, isSelected, onClick, isDragTarget }) {
  const [dragging, setDragging] = useState(false);

  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('text/plain', element.id); setDragging(true); }}
      onDragEnd={() => setDragging(false)}
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); const srcId = e.dataTransfer.getData('text/plain'); if (srcId && srcId !== element.id) onDrop(srcId, element); }}
      onClick={() => onClick(element)}
      className={`grid gap-0 px-2 py-0.5 text-[10px] font-mono cursor-grab transition-colors
        ${isSelected ? 'bg-blue-500/10' : 'hover:bg-zinc-800/30'}
        ${dragging ? 'opacity-40' : ''}
        ${isDragTarget ? 'border-t-2 border-blue-500' : ''}`}
    >
      {children}
    </div>
  );
}

// ─── Wall Table (grouped by type + thickness) ──────────────────────
function WallTable({ items = [], selected, onSelect, onUpdate, onDrop, onToggleHide }) {
  const sections = useMemo(() => groupWalls(items), [items]);

  return (
    <div className="max-h-[320px] overflow-y-auto">
      {/* Spaltenüberschrift */}
      <div className="grid grid-cols-[16px_1fr_56px_56px_40px_18px] gap-0 items-center px-2 py-1 bg-zinc-800/60 border-b border-zinc-700/40 sticky top-0 z-20">
        <span></span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold">ID</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">Länge [m]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">Stärke [cm]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">Höhe [m]</span>
        <span></span>
      </div>
      {sections.map(section => (
        <div key={section.type}>
          <div className="sticky top-0 z-10 flex items-center justify-between px-2 py-1 bg-zinc-800/80 border-b border-zinc-700/30">
            <span className="font-mono text-[9px] font-bold text-zinc-400 uppercase tracking-wider">{section.label}</span>
            <span className="font-mono text-[9px] text-zinc-500">{section.total}</span>
          </div>
          {section.groups.map(group => (
            <div key={`${section.type}-${group.thickness_mm}`}>
              <div className="flex items-center justify-between px-2 py-0.5 bg-zinc-900/50 border-b border-zinc-800/20">
                <span className="font-mono text-[9px] text-amber-500/70">{(group.thickness_mm / 10).toFixed(1)} cm</span>
                <span className="font-mono text-[9px] text-zinc-600">{group.items.length}x | {group.items.reduce((s, w) => s + (w.length_m_final || w.length_m || 0), 0).toFixed(2)} m</span>
              </div>
              {group.items.map(w => {
                const l = safeNum(w.length_m_final || w.length_m);
                const t = safeNum(w.thickness_m_final || w.thickness_m);
                const hidden = !!w.excluded_from_lv;
                return (
                  <DraggableRow key={w.id} element={w} isSelected={selected?.id === w.id}
                    onClick={onSelect} onDrop={(srcId, target) => onDrop(srcId, target, 'wall')}>
                    <div className={`grid grid-cols-[16px_1fr_56px_56px_40px_18px] gap-0 items-center ${hidden ? 'opacity-40 line-through' : ''}`}>
                      <GripVertical className="w-2.5 h-2.5 text-zinc-700" />
                      <span className="text-zinc-400 truncate text-[10px]">{safeStr(w.id)}</span>
                      <span className="text-right text-zinc-300">
                        <EditableCell value={l.toFixed(2)} className="text-zinc-300"
                          onSave={v => onUpdate(w.id, 'wall', { length_m: parseFloat(v), length_m_final: parseFloat(v), length_source: 'manual' })} />
                      </span>
                      <span className="text-right">
                        <EditableCell value={(t * 100).toFixed(1)} className="text-amber-400"
                          onSave={v => onUpdate(w.id, 'wall', { thickness_m: v / 100, thickness_m_final: v / 100, thickness_source: 'manual' })} />
                      </span>
                      <span className="text-right">
                        <EditableCell value={safeNum(w.height_m) ? safeNum(w.height_m).toFixed(2) : '2.70'} className="text-zinc-500"
                          onSave={v => onUpdate(w.id, 'wall', { height_m: parseFloat(v), height_source: 'manual' })} />
                      </span>
                      <HideBtn hidden={hidden} onClick={() => onToggleHide(w.id, 'wall')} testId={`wall-hide-${w.id}`} />
                    </div>
                  </DraggableRow>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Room Table ────────────────────────────────────────────────────
function RoomTable({ items = [], selected, onSelect }) {
  return (
    <div className="max-h-[320px] overflow-y-auto">
      <div className="grid grid-cols-[24px_1fr_64px] gap-0 px-2 py-1 text-[9px] font-mono text-zinc-600 border-b border-zinc-800/30 sticky top-0 bg-zinc-900 z-10">
        <span></span><span>Name</span><span className="text-right">Fläche</span>
      </div>
      {items.map(r => (
        <div key={r.id || r.letter} onClick={() => onSelect(r)}
          className={`grid grid-cols-[24px_1fr_64px] gap-0 px-2 py-0.5 text-[10px] font-mono cursor-pointer transition-colors ${(selected?.id === r.id || selected?.letter === r.letter) ? 'bg-blue-500/10' : 'hover:bg-zinc-800/30'}`}>
          <span className="font-bold" style={{ color: r.color || '#888' }}>{r.letter}</span>
          <span className="text-zinc-300 truncate">{r.name || '—'}</span>
          <span className="text-right text-green-400">{r.area_m2 ? `${r.area_m2} m²` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Window Table (grouped by size) ────────────────────────────────
function WindowTable({ items = [], selected, onSelect, onUpdate, onDrop, onToggleHide }) {
  const groups = useMemo(() => groupOpenings(items), [items]);

  return (
    <div className="max-h-[320px] overflow-y-auto">
      {/* Spaltenüberschrift Fenster */}
      <div className="grid grid-cols-[16px_48px_1fr_48px_48px_36px_18px] gap-0 items-center px-2 py-1 bg-zinc-800/60 border-b border-zinc-700/40 sticky top-0 z-20">
        <span></span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold">Nr.</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold">Räume</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">B [cm]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">H [cm]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">BRH</span>
        <span></span>
      </div>
      {groups.map(group => (
        <div key={`${group.w}x${group.h}`}>
          <div className="flex items-center justify-between px-2 py-0.5 bg-zinc-800/60 border-b border-zinc-700/20 sticky top-[24px] z-10">
            <span className="font-mono text-[9px] text-green-400/80 font-bold">{group.w}×{group.h || '?'} cm</span>
            <span className="font-mono text-[9px] text-zinc-600">{group.items.length}x</span>
          </div>
          {group.items.map(w => {
            const wm = w.width_m_matched || w.width_m || 0;
            const hm = w.height_m_matched || 0;
            const hidden = !!w.excluded_from_lv;
            return (
              <DraggableRow key={w.id} element={w} isSelected={selected?.id === w.id}
                onClick={onSelect} onDrop={(srcId, target) => onDrop(srcId, target, 'opening')}>
                <div className={`grid grid-cols-[16px_48px_1fr_48px_48px_36px_18px] gap-0 items-center ${hidden ? 'opacity-40 line-through' : ''}`}>
                  <GripVertical className="w-2.5 h-2.5 text-zinc-700" />
                  <span className="text-green-400">{w.gpt_label || w.id}</span>
                  <span className="text-zinc-400 truncate">{w.connects?.join(', ') || '—'}</span>
                  <span className="text-right">
                    <EditableCell value={(wm * 100).toFixed(0)} className="text-zinc-300"
                      onSave={v => onUpdate(w.id, 'opening', { width_m_matched: v / 100, width_m: v / 100, width_source: 'manual' })} />
                  </span>
                  <span className="text-right">
                    <EditableCell value={hm ? (hm * 100).toFixed(0) : ''} className="text-zinc-300"
                      onSave={v => onUpdate(w.id, 'opening', { height_m_matched: v / 100, height_source: 'manual' })} />
                  </span>
                  <span className="text-right text-zinc-500">{w.brh_m ? (w.brh_m * 100).toFixed(0) : '—'}</span>
                  <HideBtn hidden={hidden} onClick={() => onToggleHide(w.id, 'opening')} testId={`window-hide-${w.id}`} />
                </div>
              </DraggableRow>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Door Table (grouped by size + hinge) ──────────────────────────
function DoorTable({ items = [], selected, onSelect, onUpdate, onDrop, onToggleHide }) {
  const groups = useMemo(() => groupOpenings(items), [items]);

  return (
    <div className="max-h-[320px] overflow-y-auto">
      {/* Spaltenüberschrift Türen */}
      <div className="grid grid-cols-[16px_48px_1fr_42px_42px_60px_36px_18px] gap-0 items-center px-2 py-1 bg-zinc-800/60 border-b border-zinc-700/40 sticky top-0 z-20">
        <span></span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold">Nr.</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold">Aufschlag</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">B [cm]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">H [cm]</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">DIN</span>
        <span className="font-mono text-[9px] text-zinc-500 font-semibold text-right">Richt.</span>
        <span></span>
      </div>
      {groups.map(group => (
        <div key={`${group.w}x${group.h}_${group.anschlag}`}>
          <div className="flex items-center justify-between px-2 py-0.5 bg-zinc-800/60 border-b border-zinc-700/20 sticky top-[24px] z-10">
            <span className="font-mono text-[9px] text-amber-400/80 font-bold">{group.w}×{group.h || '?'} cm {group.anschlag || ''}</span>
            <span className="font-mono text-[9px] text-zinc-600">{group.items.length}x</span>
          </div>
          {group.items.map(d => {
            const wm = d.width_m_matched || d.width_m || 0;
            const hm = d.height_m_matched || 0;
            const hidden = !!d.excluded_from_lv;
            return (
              <DraggableRow key={d.id} element={d} isSelected={selected?.id === d.id}
                onClick={onSelect} onDrop={(srcId, target) => onDrop(srcId, target, 'opening')}>
                <div className={`grid grid-cols-[16px_48px_1fr_42px_42px_60px_36px_18px] gap-0 items-center ${hidden ? 'opacity-40 line-through' : ''}`}>
                  <GripVertical className="w-2.5 h-2.5 text-zinc-700" />
                  <span className="text-amber-400">{d.gpt_label || d.id}</span>
                  <span className="text-zinc-400 truncate">{d.swing_room || 'Außen'}</span>
                  <span className="text-right">
                    <EditableCell value={(wm * 100).toFixed(0)} className="text-zinc-300"
                      onSave={v => onUpdate(d.id, 'opening', { width_m_matched: v / 100, width_m: v / 100, width_source: 'manual' })} />
                  </span>
                  <span className="text-right">
                    <EditableCell value={hm ? (hm * 100).toFixed(0) : ''} className="text-zinc-300"
                      onSave={v => onUpdate(d.id, 'opening', { height_m_matched: v / 100, height_source: 'manual' })} />
                  </span>
                  <span className="text-right text-zinc-500">{d.din_anschlag || '—'}</span>
                  <span className="text-right text-zinc-600">{d.opening_direction || '—'}</span>
                  <HideBtn hidden={hidden} onClick={() => onToggleHide(d.id, 'opening')} testId={`door-hide-${d.id}`} />
                </div>
              </DraggableRow>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── LV Stats Banner (Grundfläche, AW/IW Längen+Volumen, Türen, Fenster) ─
function StatsBanner({ stats }) {
  if (!stats) return null;
  const cell = (label, value, sub) => (
    <div className="flex flex-col px-3 py-1 border-r border-zinc-800/40 last:border-r-0">
      <span className="text-[8px] text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className="font-mono text-[12px] text-zinc-100 leading-tight">{value}</span>
      {sub && <span className="text-[9px] text-zinc-500 leading-tight">{sub}</span>}
    </div>
  );
  return (
    <div className="grid grid-cols-7 bg-zinc-900/60 border-b border-zinc-800" data-testid="lv-stats-banner">
      {cell('Grundfläche', `${stats.totals.footprint_m2.toFixed(2)} m²`, `${stats.rooms.count} Räume`)}
      {cell('Außenwände', `${stats.exterior_walls.length_m.toFixed(1)} m`, `${stats.exterior_walls.count}× · ${stats.exterior_walls.volume_m3.toFixed(1)} m³`)}
      {cell('Innenwände', `${stats.interior_walls.length_m.toFixed(1)} m`, `${stats.interior_walls.count}× · ${stats.interior_walls.volume_m3.toFixed(1)} m³`)}
      {cell('Wände gesamt', `${stats.totals.wall_volume_m3.toFixed(1)} m³`, `${stats.totals.wall_area_m2.toFixed(0)} m²`)}
      {cell('Türen', stats.doors.count, `${stats.doors.total_area_m2.toFixed(2)} m²`)}
      {cell('Fenster', stats.windows.count, `${stats.windows.total_area_m2.toFixed(2)} m²`)}
      {cell('Manuell', stats.manual_positions_count, 'aktiv')}
    </div>
  );
}

// ─── Manual Positions Tab ──────────────────────────────────────────
const UNIT_OPTIONS = ['', 'Stk', 'm', 'm²', 'm³', 'kg', 't', 'lfm', 'h', 'pauschal', 'psch'];

const ManualField = React.memo(function ManualField({ label, k, type = 'text', placeholder = '', value, onChange }) {
  return (
    <label className="flex flex-col text-[9px] text-zinc-500">
      <span>{label}</span>
      <input type={type} step={type === 'number' ? '0.01' : undefined} value={value ?? ''}
        onChange={e => onChange(k, type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
        data-testid={`manual-input-${k}`} placeholder={placeholder}
        className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-[10px] font-mono" />
    </label>
  );
});

const ManualSelect = React.memo(function ManualSelect({ label, k, value, onChange, options, placeholder }) {
  return (
    <label className="flex flex-col text-[9px] text-zinc-500">
      <span>{label}</span>
      <select value={value ?? ''} onChange={e => onChange(k, e.target.value || null)}
        data-testid={`manual-input-${k}`}
        className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-[10px] font-mono">
        <option value="">{placeholder || '–'}</option>
        {options.filter(o => o).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
});

// HINWEIS: Komponente OUTSIDE definiert, sonst Re-Mount + Focus-Verlust pro Tastendruck
const QuickCalcInp = React.memo(function QuickCalcInp({ label, value, onChange, testId }) {
  return (
    <label className="flex flex-col text-[9px] text-zinc-500">
      <span>{label}</span>
      <input type="number" step="0.01" value={value} onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-[10px] font-mono" />
    </label>
  );
});

function QuickCalc({ onApply }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [areaInput, setAreaInput] = useState('');
  const [height, setHeight] = useState('');
  const [count, setCount] = useState('');

  const L = parseFloat(length) || 0;
  const W = parseFloat(width) || 0;
  const Ad = parseFloat(areaInput) || 0;
  const H = parseFloat(height) || 0;
  const N = parseFloat(count) || 1;

  // Fläche: direkte Eingabe hat Vorrang vor L×B
  const area = Ad > 0
    ? round2(Ad * N)
    : (L && W) ? round2(L * W * N) : null;
  const volume = (area && H) ? round2(area * H) : null;
  const linearLength = (L && !W && !Ad) ? round2(L * N) : null;

  const apply = () => {
    const patch = {};
    if (linearLength) { patch.length_m = linearLength; if (!patch.unit) patch.unit = 'm'; if (!patch.quantity) patch.quantity = linearLength; }
    if (area) { patch.area_m2 = area; if (!patch.unit) patch.unit = 'm²'; if (!patch.quantity) patch.quantity = area; }
    if (volume) { patch.volume_m3 = volume; if (!patch.unit) patch.unit = 'm³'; if (!patch.quantity) patch.quantity = volume; }
    onApply(patch);
    setLength(''); setWidth(''); setAreaInput(''); setHeight(''); setCount('');
  };

  return (
    <div className="border border-emerald-700/30 rounded p-2 bg-emerald-900/10 mb-2" data-testid="manual-quickcalc">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-wider text-emerald-300">⚡ Schnellrechner</span>
        <span className="text-[9px] text-zinc-600">Estrich, Bodenplatte, Putz, Mauerwerk …</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        <QuickCalcInp label="Länge m" value={length} onChange={setLength} testId="qc-length" />
        <QuickCalcInp label="Breite m" value={width} onChange={setWidth} testId="qc-width" />
        <QuickCalcInp label="Fläche m²" value={areaInput} onChange={setAreaInput} testId="qc-area" />
        <QuickCalcInp label="Höhe m" value={height} onChange={setHeight} testId="qc-height" />
        <QuickCalcInp label="× Stück" value={count} onChange={setCount} testId="qc-count" />
      </div>
      <div className="mt-0.5 text-[8.5px] text-zinc-600 font-mono">
        Tipp: Fläche m² direkt eingeben → überschreibt Länge × Breite. Mit Höhe → Volumen.
      </div>
      <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2 items-center">
        <div className="flex gap-3 text-[10px] font-mono text-zinc-300">
          {linearLength && <span><span className="text-zinc-500">Länge:</span> <b className="text-emerald-300">{linearLength.toFixed(2)} m</b></span>}
          {area && <span><span className="text-zinc-500">Fläche:</span> <b className="text-emerald-300">{area.toFixed(2)} m²</b></span>}
          {volume && <span><span className="text-zinc-500">Volumen:</span> <b className="text-emerald-300">{volume.toFixed(2)} m³</b></span>}
          {!area && !volume && !linearLength && <span className="text-zinc-600">Werte eingeben…</span>}
        </div>
        <button onClick={apply} disabled={!area && !volume && !linearLength} data-testid="qc-apply"
          className="px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[10px] font-semibold whitespace-nowrap">
          → Übernehmen
        </button>
      </div>
    </div>
  );
}
function round2(v) { return Math.round(v * 100) / 100; }

function ManualPositionsTab({ analysisId, positions, onChanged }) {
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);

  const setField = useCallback((k, v) => {
    setDraft(d => ({ ...d, [k]: v }));
  }, []);

  const applyCalc = useCallback((patch) => {
    setDraft(d => ({ ...d, ...patch }));
  }, []);

  const submit = async () => {
    if (!draft.title) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/analyses/${analysisId}/manual-positions`, draft);
      setDraft({});
      onChanged();
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  const update = async (id, patch) => {
    try { await axios.put(`${API}/api/analyses/${analysisId}/manual-positions/${id}`, patch); onChanged(); }
    catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!await modal.confirm('Position löschen?', { title: 'Löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try { await axios.delete(`${API}/api/analyses/${analysisId}/manual-positions/${id}`); onChanged(); }
    catch (e) { console.error(e); }
  };

  return (
    <div className="max-h-[420px] overflow-y-auto p-2 space-y-2" data-testid="manual-positions-tab">
      <QuickCalc onApply={applyCalc} />

      {/* Add form */}
      <div className="border border-zinc-700 rounded p-2 bg-zinc-900/40">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5">Neue Position</div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="col-span-2">
            <ManualField label="Titel *" k="title" placeholder="z.B. Estrich EG" value={draft.title} onChange={setField} />
          </div>
          <ManualField label="Kategorie" k="category" placeholder="Bodenbelag" value={draft.category} onChange={setField} />
          <ManualField label="Menge" k="quantity" type="number" value={draft.quantity} onChange={setField} />
          <ManualSelect label="Einheit" k="unit" value={draft.unit} onChange={setField} options={UNIT_OPTIONS} placeholder="Einheit wählen" />
          <ManualField label="EP €" k="unit_price_eur" type="number" value={draft.unit_price_eur} onChange={setField} />
          <ManualField label="Länge m" k="length_m" type="number" value={draft.length_m} onChange={setField} />
          <ManualField label="Fläche m²" k="area_m2" type="number" value={draft.area_m2} onChange={setField} />
          <ManualField label="Volumen m³" k="volume_m3" type="number" value={draft.volume_m3} onChange={setField} />
        </div>
        <label className="block mt-1.5">
          <span className="text-[9px] text-zinc-500">Beschreibung</span>
          <textarea value={draft.description ?? ''}
            onChange={e => setField('description', e.target.value)}
            data-testid="manual-input-description" rows={2}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-[10px] font-mono" />
        </label>
        <div className="mt-1.5 flex justify-end">
          <button onClick={submit} disabled={adding || !draft.title} data-testid="manual-add-btn"
            className="flex items-center gap-1 px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-[10px] font-semibold">
            <Plus className="w-2.5 h-2.5" /> {adding ? 'Speichere…' : 'Hinzufügen'}
          </button>
        </div>
      </div>

      {/* List */}
      {positions.length === 0 ? (
        <div className="text-zinc-600 text-[10px] text-center py-4">Noch keine manuellen Positionen.</div>
      ) : (
        <div className="space-y-0.5">
          {positions.map(p => {
            const total = (p.quantity || 0) * (p.unit_price_eur || 0);
            return (
              <div key={p.id} data-testid={`manual-row-${p.id}`}
                className={`grid grid-cols-[1fr_56px_36px_56px_56px_44px] gap-1 px-2 py-1 rounded border border-zinc-800 text-[10px] font-mono items-center ${p.excluded_from_lv ? 'opacity-40 line-through bg-zinc-900/30' : 'bg-zinc-900/60 hover:bg-zinc-900'}`}>
                <div className="truncate">
                  <span className="text-zinc-100">{p.title || '–'}</span>
                  {p.category && <span className="ml-1 text-[9px] text-zinc-500">[{p.category}]</span>}
                  {p.description && <div className="text-[9px] text-zinc-500 truncate">{p.description}</div>}
                </div>
                <span className="text-right text-zinc-300">{p.quantity ?? '–'}</span>
                <span className="text-zinc-500 text-center">{p.unit || '–'}</span>
                <span className="text-right text-zinc-300">{p.unit_price_eur != null ? `${p.unit_price_eur.toFixed(2)} €` : '–'}</span>
                <span className="text-right text-zinc-200">{total ? `${total.toFixed(2)} €` : '–'}</span>
                <div className="flex justify-end gap-0.5">
                  <button onClick={() => update(p.id, { excluded_from_lv: !p.excluded_from_lv })}
                    data-testid={`manual-hide-${p.id}`}
                    className={`p-0.5 rounded ${p.excluded_from_lv ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                    {p.excluded_from_lv ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                  </button>
                  <button onClick={() => del(p.id)} data-testid={`manual-del-${p.id}`}
                    className="p-0.5 rounded bg-red-900/40 text-red-300 hover:bg-red-900/60">
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Hide-Toggle Button (klein, in jeder Zeile) ───────────────────
function HideBtn({ hidden, onClick, testId }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testId}
      className={`p-0.5 rounded transition-colors ${hidden ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60' : 'text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800'}`}
      title={hidden ? 'Wieder einblenden (im LV aktiv)' : 'Ausblenden (nicht im LV)'}>
      {hidden ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
    </button>
  );
}

// ─── Main Component ────────────────────────────────────────────────
export default function FloorElementList({ analysisId }) {
  const modal = useModal();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState('walls');
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [manualPositions, setManualPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formatSource, setFormatSource] = useState(null);
  const [lvEditorOpen, setLvEditorOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [an, ls, mp] = await Promise.all([
        fetch(`${API}/api/analyses/${analysisId}`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/analyses/${analysisId}/lv-stats`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/analyses/${analysisId}/manual-positions`).then(r => r.ok ? r.json() : { positions: [] }),
      ]);
      setData(an);
      setStats(ls);
      setManualPositions(mp?.positions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [analysisId]);

  useEffect(() => { if (expanded && !data) fetchData(); }, [expanded, data, fetchData]);

  const reloadStatsAndPositions = useCallback(async () => {
    try {
      const [ls, mp] = await Promise.all([
        fetch(`${API}/api/analyses/${analysisId}/lv-stats`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/analyses/${analysisId}/manual-positions`).then(r => r.ok ? r.json() : { positions: [] }),
      ]);
      setStats(ls);
      setManualPositions(mp?.positions || []);
    } catch (e) { console.error(e); }
  }, [analysisId]);

  const elements = useMemo(() => {
    if (!data?.svg_data) return {};
    const o = data.svg_data.openings || [];
    return {
      walls: data.svg_data.wall_lines || [],
      rooms: data.svg_data.rooms || [],
      windows: o.filter(x => x.type === 'window'),
      doors: o.filter(x => x.type === 'door'),
    };
  }, [data]);

  const counts = { walls: elements.walls?.length || 0, rooms: elements.rooms?.length || 0, windows: elements.windows?.length || 0, doors: elements.doors?.length || 0 };

  const handleUpdate = useCallback(async (elId, elType, updates) => {
    if (!data?.svg_data) return;
    const svgData = { ...data.svg_data };
    if (elType === 'wall') {
      svgData.wall_lines = svgData.wall_lines.map(w => w.id === elId ? { ...w, ...updates } : w);
    } else {
      svgData.openings = svgData.openings.map(o => o.id === elId ? { ...o, ...updates } : o);
    }
    try {
      await fetch(`${API}/api/analyses/${analysisId}/svg-data`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ svg_data: svgData }),
      });
      setData(prev => ({ ...prev, svg_data: svgData }));
      reloadStatsAndPositions();
    } catch (e) { console.error(e); }
  }, [data, analysisId, reloadStatsAndPositions]);

  const handleToggleHide = useCallback(async (elId, elType) => {
    const collection = elType === 'wall' ? data?.svg_data?.wall_lines : data?.svg_data?.openings;
    const el = collection?.find(x => x.id === elId);
    const newHidden = !el?.excluded_from_lv;
    handleUpdate(elId, elType, { excluded_from_lv: newHidden });
  }, [data, handleUpdate]);

  // Drag & Drop: adopt target group's properties
  const handleDrop = useCallback((srcId, target, elType) => {
    if (elType === 'wall') {
      const t = target.thickness_m_final || target.thickness_m;
      if (t) handleUpdate(srcId, 'wall', { thickness_m: t, thickness_m_final: t, thickness_source: 'manual', wall_type: target.wall_type });
    } else {
      const updates = {};
      if (target.width_m_matched) { updates.width_m_matched = target.width_m_matched; updates.width_m = target.width_m_matched; }
      if (target.height_m_matched) updates.height_m_matched = target.height_m_matched;
      if (target.din_anschlag) { updates.din_anschlag = target.din_anschlag; updates.hinge_side = target.hinge_side; }
      updates.width_source = 'manual';
      handleUpdate(srcId, 'opening', updates);
    }
  }, [handleUpdate]);

  const handleSelect = useCallback((el) => {
    if (formatSource) {
      if (formatSource.id === el.id) return;
      handleDrop(el.id, formatSource, formatSource.wall_type !== undefined ? 'wall' : 'opening');
      setFormatSource(null);
      return;
    }
    setSelected(el);
  }, [formatSource, handleDrop]);

  if (!expanded) {
    return (
      <button data-testid={`expand-elements-${analysisId}`} onClick={() => setExpanded(true)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-800/30 transition-colors border-t border-zinc-800/20">
        <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
        <span className="font-mono text-xs text-blue-400 font-medium">Kontrolle der Elemente</span>
      </button>
    );
  }

  return (
    <div className="border-t border-zinc-800/20 bg-zinc-900/30" data-testid={`element-list-${analysisId}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1">
        <button onClick={() => { setExpanded(false); setSelected(null); setFormatSource(null); }} className="flex items-center gap-1">
          <ChevronDown className="w-3 h-3 text-zinc-500" />
          <span className="font-mono text-[10px] text-zinc-400">Kontrolle der Elemente</span>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={() => setLvEditorOpen(true)} data-testid="lv-editor-btn"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono bg-zinc-800 hover:bg-zinc-700 text-emerald-300 font-semibold border border-emerald-500/30"
            title="LV bearbeiten und exportieren (Texte, Mengen, Reihenfolge anpassen)">
            <FileDown className="w-2.5 h-2.5" /> LV-Editor
          </button>
          <button onClick={async () => {
              try {
                const r = await axios.get(`${API}/api/analyses/${analysisId}/export-langtext-lv`, { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url; a.download = `Langtext-LV_${analysisId.slice(0, 8)}.pdf`; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch (e) { modal.alert('Langtext-LV-Export fehlgeschlagen: ' + (e.response?.data?.detail || e.message), { title: 'Export-Fehler', type: 'warning' }); }
            }} data-testid="langtext-lv-export-btn"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono bg-emerald-600/80 hover:bg-emerald-500 text-white font-semibold border border-emerald-500/40"
            title="Langtext-Leistungsverzeichnis direkt als PDF (ohne Editor)">
            <FileDown className="w-2.5 h-2.5" /> LV-PDF
          </button>
          <button onClick={fetchData} disabled={loading} data-testid="lv-refresh-btn"
            className="p-0.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-zinc-300 disabled:opacity-50"
            title="Aktualisieren (zieht neueste Daten aus dem Plan-Editor)">
            <RefreshCw className={`w-2.5 h-2.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {selected && (
            <button data-testid="format-transfer-btn"
              onClick={() => setFormatSource(formatSource ? null : selected)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${formatSource ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 border border-zinc-700/50'}`}>
              <Paintbrush className="w-2.5 h-2.5" />
              {formatSource ? 'Ziel klicken' : 'Format'}
            </button>
          )}
          {formatSource && (
            <button onClick={() => setFormatSource(null)} className="p-0.5 hover:bg-zinc-700 rounded">
              <X className="w-2.5 h-2.5 text-zinc-500" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800/30">
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); setFormatSource(null); }}
            className={`flex-1 py-1 font-mono text-[10px] transition-colors border-b-2 ${tab === t.key ? 'text-zinc-200 border-blue-500' : 'text-zinc-600 border-transparent hover:text-zinc-400'}`}
            data-testid={`floor-tab-${t.key}`}>
            {t.label} <span className="text-zinc-600">{t.key === 'manual' ? manualPositions.length : counts[t.key]}</span>
          </button>
        ))}
      </div>

      {/* LV Stats Banner */}
      <StatsBanner stats={stats} />

      {loading ? (
        <div className="py-4 text-center font-mono text-[10px] text-zinc-600">Laden...</div>
      ) : (
        <div className="flex">
          {/* Table */}
          <div className="flex-1 min-w-0">
            {tab === 'walls' && <WallTable items={elements.walls} selected={selected} onSelect={handleSelect} onUpdate={handleUpdate} onDrop={handleDrop} onToggleHide={handleToggleHide} />}
            {tab === 'rooms' && <RoomTable items={elements.rooms} selected={selected} onSelect={handleSelect} />}
            {tab === 'windows' && <WindowTable items={elements.windows} selected={selected} onSelect={handleSelect} onUpdate={handleUpdate} onDrop={handleDrop} onToggleHide={handleToggleHide} />}
            {tab === 'doors' && <DoorTable items={elements.doors} selected={selected} onSelect={handleSelect} onUpdate={handleUpdate} onDrop={handleDrop} onToggleHide={handleToggleHide} />}
            {tab === 'manual' && <ManualPositionsTab analysisId={analysisId} positions={manualPositions} onChanged={reloadStatsAndPositions} />}
          </div>

          {/* High-res preview (nicht für Manuell-Tab) */}
          {tab !== 'manual' && (
            <div className="w-[420px] flex-shrink-0 border-l border-zinc-800/30 bg-zinc-950/50">
              <div className="h-[320px]">
                <CropPreview imageB64={data?.original_image} imageSize={data?.image_size} element={selected} />
              </div>
              {selected && (
                <div className="px-2 py-0.5 font-mono text-[9px] text-zinc-500 truncate text-center border-t border-zinc-800/20">
                  {selected.gpt_label || selected.name || selected.letter || selected.id}
                  {selected.wall_type && <span className="ml-2 text-zinc-600">| {selected.wall_type === 'exterior' ? 'AW' : 'IW'} {((selected.thickness_m_final || selected.thickness_m || 0) * 100).toFixed(1)}cm</span>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <LVEditorModal
        open={lvEditorOpen}
        onClose={() => setLvEditorOpen(false)}
        analysisId={analysisId}
        elementIds={[]}
      />
    </div>
  );
}
